import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { AdminSourceView, CreateSourceInput, UpdateSourceInput } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost } from '../../../lib/api-client';
import { APP_SETTINGS_KEY } from '../../../lib/app-settings';
import type { RowCache } from '../components/simple-list/use-row-changes';

// ADMIN-08 (S31 Q18; Admin redesign batch 2). Changes go through the simple list's row engine; every picker reads
// sources from the app settings, so those are refetched after a change too.

export const SOURCES_KEY = ['admin', 'sources'] as const;

export function useSources(isEnabled: boolean): UseQueryResult<readonly AdminSourceView[], ApiError> {
  return useQuery<readonly AdminSourceView[], ApiError>({
    queryKey: SOURCES_KEY,
    queryFn: () => apiGet<readonly AdminSourceView[]>('/admin/sources'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export const SOURCE_ROWS: RowCache<readonly AdminSourceView[], AdminSourceView> = {
  queryKey: SOURCES_KEY,
  rowsOf: (rows) => rows,
  withRows: (_rows, rows) => rows,
  idOf: (row) => row.key,
  alsoRefresh: [APP_SETTINGS_KEY],
};

/** The version the server gave the source, from the list it answers with. */
function versionOf(rows: readonly AdminSourceView[], key: string): number {
  const row = rows.find((source) => source.key === key);
  if (!row) throw new ApiError({ code: 'not_found', message: "That source doesn't exist any more.", status: 404 });
  return row.version;
}

export const sourcesApi = {
  change: (key: string, change: Omit<UpdateSourceInput, 'version'>) => async (version: number) => versionOf(await apiPatch<readonly AdminSourceView[]>(`/admin/sources/${key}`, { ...change, version }), key),
  create: async (input: CreateSourceInput): Promise<readonly AdminSourceView[]> => apiPost<readonly AdminSourceView[]>('/admin/sources', input),
};
